/* @flow */

import * as React from 'react';

import classNames from 'classnames';

import {colors} from '../colors';
import {FlashBanner} from '../flash-banner';
import {Stack, Divider} from '../layout';
import {Link} from '../link';
import {LoadingPage} from '../loading-page';
import {Switch} from '../switch';
import {Body} from '../typography';

import './whats-new-banner.module.css';

type SwitchProps = {|
    onClick: () => void,
    isOn: boolean,
    label: string,
    tooltipText?: string,
    labelAsToggle?: boolean,
|};

type Props = {|
    /** Main message to display to the user */
    message: string,
    /** Label for the onboarding/info link (defaults to "What’s new?") */
    primaryLabel?: string,
    /** Label for the feedback/survey link (defaults to "Tell us what you think") */
    feedbackLabel?: string,
    onPrimaryClick?: () => void,
    onOpenSurvey?: (e?: SyntheticEvent<HTMLAnchorElement>) => void,
    isLoadingSurvey?: boolean,
    /** Props for an optional switch for toggle behavior */
    switchProps?: SwitchProps,

    // FlashBanner props
    onHidden?: () => void,
    hasBorder?: boolean,
    lessPadding?: boolean,
    animate?: boolean,
|};

export function WhatsNewBanner(props: Props) {
    const {
        message,
        primaryLabel = 'What’s new?',
        feedbackLabel = 'Tell us what you think',
        onPrimaryClick,
        onOpenSurvey,
        isLoadingSurvey,
        switchProps,
        ...flashBannerProps
    } = props;

    const switchClassNames = classNames('flex gap-4 pl-4', {
        'pl-24': isLoadingSurvey,
    });

    return (
        <FlashBanner type='info' {...flashBannerProps}>
            <Stack spacing={6} isHorizontal={true} alignCenter={true}>
                <Body color='dark'>{message}</Body>
                {onPrimaryClick && (
                    <Link onClick={onPrimaryClick} variant='primary' isBold={true}>
                        {primaryLabel}
                    </Link>
                )}
                {onOpenSurvey && (
                    <>
                        <Divider variant='line--vertical' color={colors.black} spacing={2} />
                        <Link onClick={onOpenSurvey} variant='primary' isBold={true}>
                            {feedbackLabel}
                            {isLoadingSurvey && (
                                <div styleName='loading-survey-container'>
                                    <LoadingPage size={24} padding={0} fill={colors.orange} />
                                </div>
                            )}
                        </Link>
                    </>
                )}
                {switchProps && (
                    <div className={switchClassNames}>
                        <Switch
                            isOn={switchProps.isOn}
                            onChange={switchProps.onClick}
                            isRounded={true}
                            isSmall={true}
                            tooltipText={switchProps.tooltipText}
                        />
                        {switchProps.labelAsToggle ? (
                            <Link onClick={switchProps.onClick}>{switchProps.label}</Link>
                        ) : (
                            <Body color='dark'>{switchProps.label}</Body>
                        )}
                    </div>
                )}
            </Stack>
        </FlashBanner>
    );
}
